// IHG Custom Buyer Briefs demo — list and detail views.
// Completely self-contained, frontend-only, non-functional demo based on
// Buyer Briefs V2. No backend calls: data comes from the in-memory store in
// ihgDemoData.ts. Seeded briefs render their canned outputs; briefs created
// through the wizard stay in a generating state forever.

import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Empty, Input, Result, Row, Space, Spin } from "antd";
import { ArrowLeftOutlined, LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { usePDF } from "react-to-pdf";
import "./ihgBrief.less";
import { IhgBrief, IhgBriefStatus } from "./IhgDemoTypes";
import { createIhgBrief, getIhgBrief, getIhgBriefs } from "./ihgDemoData";
import { IhgBriefCard } from "./components/IhgBriefCard";
import IhgBriefWizard from "./IhgBriefWizard";
import { useBriefStickyNav } from "../../common/useBriefStickyNav";
import classes from "./IhgBriefListView.module.less";
import detailClasses from "./IhgBriefDetailView.module.less";

export const IhgBriefListView: React.FC = () => {
    const [wizardOpen, setWizardOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [, setVersion] = useState(0);

    const briefs = getIhgBriefs();

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return briefs;
        return briefs.filter(
            (b) =>
                b.subject.toLowerCase().includes(term) ||
                b.category.toLowerCase().includes(term)
        );
    }, [briefs, search]);

    return (
        <Card bordered={false}>
            <Row justify="space-between" className={classes.header}>
                <div>
                    <div className={classes.title}>Buyer Briefs</div>
                    <div className={classes.subtitle}>
                        Vendor diligence and category benchmarks covering market position,
                        pricing, and risk.
                    </div>
                </div>
                <Button
                    type="primary"
                    size="large"
                    className={classes.creator}
                    onClick={() => setWizardOpen(true)}
                >
                    + Buyer Brief
                </Button>
            </Row>
            <Space align="center" size={16} className={classes.tools}>
                <Input
                    className={classes.search}
                    placeholder="Search..."
                    prefix={<SearchOutlined />}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Space>
            {filtered.length > 0 ? (
                filtered.map((b) => <IhgBriefCard key={b.id} brief={b} />)
            ) : (
                <Empty />
            )}
            <IhgBriefWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onComplete={(input) => {
                    createIhgBrief(input);
                    setWizardOpen(false);
                    setVersion((v) => v + 1);
                }}
            />
        </Card>
    );
};

export const IhgBriefDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const brief: IhgBrief | undefined = getIhgBrief(Number(id));
    const isProcessing = brief?.status === IhgBriefStatus.PROCESSING;

    const { toPDF, targetRef } = usePDF({
        filename: `buyer-brief-${brief?.subject || id}.pdf`,
    });

    const cleanHtml = useMemo(() => {
        // Styles come from ihgBrief.less (scoped under .il-ihg-brief), so any
        // <style> blocks are forbidden; inline style= attributes are allowed.
        return DOMPurify.sanitize(brief?.briefHtml || "", {
            ADD_ATTR: ["target", "rel"],
            FORCE_BODY: true,
            WHOLE_DOCUMENT: false,
            FORBID_TAGS: ["style"],
        });
    }, [brief?.briefHtml]);

    // Replicates the designer's standalone <script> (stripped by DOMPurify):
    // sticky header measurement, active-section tracking, and sidenav clicks —
    // shared with the real buyer brief view.
    useBriefStickyNav(targetRef, {
        enabled: !!brief && !isProcessing,
        contentHtml: cleanHtml,
        navLinkSelector: ".brief-sidenav-btn",
        defaultSectionId: "recommendation",
    });

    if (!brief) {
        return <Result status="404" title="Brief not found" />;
    }

    const renderProcessing = () => (
        <div style={{ textAlign: "center", padding: 80 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
            <h3 style={{ marginTop: 24 }}>Generating Brief for {brief.subject}</h3>
            <p style={{ color: "#666", marginTop: 8 }}>Conducting AI research...</p>
        </div>
    );

    const renderCompleted = () => (
        <div ref={targetRef}>
            <div className="il-ihg-brief" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
        </div>
    );

    return (
        <Card
            className={detailClasses.detailCard}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/superadmin/demo/ihg-brief")}
                    />
                    <span>{brief.subject}</span>
                </div>
            }
            extra={
                !isProcessing && (
                    <Button onClick={() => toPDF()} type="primary">
                        Export PDF
                    </Button>
                )
            }
        >
            {isProcessing ? renderProcessing() : renderCompleted()}
        </Card>
    );
};
